import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Button, Select, Segmented, Table, Alert, StatCard, PageHeader, Tag, Tooltip, EntityAvatar, theme, message } from '../../ui';
import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, TrendingDown, Minus, CalendarRange, Sigma, Gauge, History, TriangleAlert, Play, Info, Package,
} from 'lucide-react';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { themedCatAxis, themedValAxis, themedLegendText, themedTooltip } from '../../lib/chartTheme';
import { PALETTE } from '../../lib/palette';
import { inr, inr2, fmtQty } from '../../lib/format';
import { productImage } from '../../lib/imageUrls';

const day10 = (v: any) => String(v ?? '').slice(0, 10); // normalise day/date keys to YYYY-MM-DD

/** Horizons worth a click. The server accepts 7–90; these are the questions people actually ask. */
const HORIZONS = [7, 14, 30, 60, 90];

export function ForecastsPage() {
  const { token } = theme.useToken();
  const [familyKey, setFamilyKey] = useState<string | undefined>();
  const [productId, setProductId] = useState<string | undefined>();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<any>(null);
  /** What the SHOWN result was run for — the controls can move after a run, and the numbers on screen
   *  must keep saying which product and horizon produced them. */
  const [ran, setRan] = useState<{ productId?: string; days: number; productLabel?: string } | null>(null);
  const [running, setRunning] = useState(false);
  /* The page opens on its own answer.
   *
   * An empty forecast page is a headline, three controls and a screen of nothing — and the default
   * question ("what does the whole business look like over the next 30 days?") is the one almost
   * everyone arrives with. It is a cheap read-only query, so run it once on load and let the controls
   * refine it, rather than making everyone click Run to see anything at all. */
  const [booted, setBooted] = useState(false);

  /* Each KPI is a QUESTION, so each one opens its own answer on the chart or in the table below —
     a tile that only restates a number the chart already draws is a label, not a control. Every one
     of these is a reversible view toggle: clicking again puts it back. */
  const [showAvg, setShowAvg] = useState(false);      // draw the daily-average line
  const [showPrev, setShowPrev] = useState(false);    // overlay the same span of history behind it
  const [histOnly, setHistOnly] = useState(false);    // zoom to the history the model learned from
  const [focusPeak, setFocusPeak] = useState(false);  // mark the busiest day and jump to its row
  const tableRef = useRef<HTMLDivElement | null>(null);

  const { data: products } = useQuery({
    // Active products only — the forecast target picker must not list archived/deactivated SKUs.
    queryKey: ['products-for-forecast', 'active'],
    queryFn: () => requestAllPages('/products', { active: 'true' }),
  });

  /*
   * PRODUCT THEN VARIANT, the way every other picker in the app does it.
   *
   * A flat list put "AGN 500ml", "AGN 1L", "AGN LTE 250ml", "AGN LTE 500ml", "AGN LTE 1L" beside each
   * other as five unrelated entries, so choosing a product meant reading five near-identical strings
   * and spotting the size at the end of each. Siblings share `group_code`, so the family is a real
   * thing in the data, not a guess — pick the family first, then the size.
   */
  const families = useMemo(() => {
    type Family = { key: string; name: string; image?: string; variants: any[] };
    const m = new Map<string, Family>();
    for (const p of ((products?.data ?? []) as any[])) {
      const key = p.groupCode ? `g:${p.groupCode}` : `n:${p.name}`;
      const e: Family = m.get(key) ?? { key, name: p.name, image: productImage(p), variants: [] };
      if (!e.image) e.image = productImage(p);
      e.variants.push(p);
      m.set(key, e);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const selFamily = families.find((f) => f.key === familyKey);
  const variants = selFamily?.variants ?? [];
  const selProduct = variants.find((v: any) => String(v.id) === String(productId))
    ?? (products?.data ?? []).find((p: any) => String(p.id) === String(productId));

  /** Picking a family clears the variant — except where there is only one, which needs no second step. */
  const onFamily = (k?: string) => {
    setFamilyKey(k);
    const fam = families.find((f) => f.key === k);
    setProductId(fam && fam.variants.length === 1 ? String(fam.variants[0].id) : undefined);
  };

  const run = async () => {
    setRunning(true);
    try {
      const r = await request<any>({ url: '/ai/forecast/sales', method: 'POST', data: { productId, days } });
      setData(r);
      // Name what was run: the family plus the variant, so the result's title survives the controls moving.
      setRan({
        productId, days,
        productLabel: selProduct
          ? [selFamily?.name ?? selProduct.name, selProduct.variantLabel].filter(Boolean).join(' · ')
          : undefined,
      });
    } catch (e: any) {
      message.error(e.response?.data?.error?.message ?? 'Forecast failed');
    } finally {
      setRunning(false);
    }
  };

  /*
   * THE CONTROLS ARE THE QUESTION, so changing one re-asks it.
   *
   * Picking "7d" and still being shown thirty days of projection until you find the Run button is the
   * control lying about what is on screen. This fires on mount as well (see `booted`), so the page
   * opens on the default answer instead of an empty frame, and every later change to the product,
   * the size or the horizon re-runs it immediately — no Apply step.
   */
  useEffect(() => {
    setBooted(true);
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, days]);

  /*
   * WHICH NUMBER IS BEING FORECAST.
   *
   * The endpoint switches measure with the filter: with no product it sums ORDER VALUE (₹), and with a
   * product it sums that product's QUANTITY. Same chart, same axis, two entirely different units — and
   * nothing on the page used to say which one you were looking at, so a ₹ figure and a bag count were
   * drawn identically. Everything below reads its unit from here.
   */
  const isQty = !!ran?.productId;
  const unitLabel = isQty ? 'units' : 'order value';
  const fmt = (v: any) => (isQty ? fmtQty(v) : inr2(v));
  const fmtShort = (v: any) => (isQty ? fmtQty(v) : inr(v));

  const history: any[] = data?.history ?? [];
  const forecast: any[] = data?.forecast ?? [];

  const stats = useMemo(() => {
    const hist = history.map((h) => Number(h.total ?? 0));
    const fc = forecast.map((f) => Number(f.forecast ?? 0));
    const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);
    const total = sum(fc);
    // Like-for-like: the same number of days of history as the horizon being forecast, so the
    // comparison is a rate against a rate rather than 30 days against 90.
    const tail = hist.slice(-forecast.length || -1);
    const prior = sum(tail);
    const change = prior > 0 ? ((total - prior) / prior) * 100 : null;
    const peak = forecast.reduce((best: any, f: any) => (!best || Number(f.forecast) > Number(best.forecast) ? f : best), null);
    // How wide the 95% band is relative to the line itself. Sparse, spiky history makes this enormous,
    // and a forecast whose range dwarfs its own value is a guess — the page has to admit that.
    const bandWidth = fc.length
      ? sum(forecast.map((f: any) => Number(f.upper ?? 0) - Number(f.lower ?? 0))) / fc.length
      : 0;
    const avg = fc.length ? total / fc.length : 0;
    return {
      total, avg, change, peak, prior, bandWidth,
      shaky: avg > 0 && bandWidth > avg * 2,
      histFrom: history[0]?.day ? day10(history[0].day) : null,
      histTo: history[history.length - 1]?.day ? day10(history[history.length - 1].day) : null,
    };
  }, [history, forecast, data]);

  const option = useMemo(() => {
    if (!data || (!history.length && !forecast.length)) return null;
    const histDates = history.map((h) => day10(h.day));
    const fcDates = forecast.map((f) => day10(f.date));
    // "History it learned from" drops the projected half so the 90 days of actuals fill the plot.
    const axis = histOnly ? histDates : [...histDates, ...fcDates];
    const pad = history.map(() => null);
    const lower = forecast.map((f: any) => Number(f.lower ?? 0));
    // The band is drawn as a transparent floor plus a stacked ribbon — one shaded region, not two
    // stray lines the reader has to mentally join up.
    const span = forecast.map((f: any, i: number) => Math.max(0, Number(f.upper ?? 0) - lower[i]));
    return {
      grid: { left: 8, right: 16, top: 34, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category', data: axis, boundaryGap: false, ...themedCatAxis(token),
        axisLabel: { color: token.colorTextTertiary, formatter: (v: string) => dayjs(v).format('D MMM') },
      },
      yAxis: {
        type: 'value', ...themedValAxis(token),
        axisLabel: { color: token.colorTextTertiary, formatter: (v: number) => fmtShort(v) },
      },
      tooltip: {
        trigger: 'axis', ...themedTooltip(token),
        formatter: (ps: any[]) => {
          if (!ps?.length) return '';
          const date = dayjs(ps[0].axisValue).format('ddd D MMM YYYY');
          const get = (n: string) => ps.find((p) => p.seriesName === n);
          const h = get('Actual'); const f = get('Forecast'); const lo = get('_lower'); const band = get('Likely range');
          const line: string[] = [`<b>${date}</b>`];
          if (h && h.value != null) line.push(`Actual: <b>${fmt(h.value)}</b>`);
          if (f && f.value != null) {
            line.push(`Forecast: <b>${fmt(f.value)}</b>`);
            if (lo?.value != null && band?.value != null) {
              line.push(`<span style="opacity:.7">Likely between ${fmt(lo.value)} and ${fmt(Number(lo.value) + Number(band.value))}</span>`);
            }
          }
          return line.join('<br/>');
        },
      },
      legend: {
        top: 0, textStyle: themedLegendText(token),
        // The two band helpers are plumbing, not series someone chooses to see.
        data: ['Actual', 'Forecast', 'Likely range', ...(showPrev ? ['Same span behind'] : [])],
      },
      series: [
        // The previous span replayed against the forecast dates, so "+12% vs behind" can be SEEN
        // rather than taken on trust. Drawn first, so it sits under the real lines.
        ...(showPrev && !histOnly ? [{
          name: 'Same span behind', type: 'line', smooth: true, symbol: 'none',
          lineStyle: { width: 2, type: 'dotted', color: token.colorTextQuaternary },
          itemStyle: { color: token.colorTextQuaternary },
          data: [...pad, ...history.slice(-forecast.length).map((h: any) => Number(h.total ?? 0))],
        }] : []),
        {
          name: '_lower', type: 'line', stack: 'band', symbol: 'none', silent: true,
          lineStyle: { opacity: 0 }, areaStyle: { opacity: 0 },
          data: [...pad, ...lower], legendHoverLink: false,
        },
        {
          name: 'Likely range', type: 'line', stack: 'band', symbol: 'none',
          lineStyle: { opacity: 0 },
          areaStyle: { color: PALETTE.harvest[400], opacity: 0.16 },
          data: [...pad, ...span],
        },
        {
          name: 'Actual', type: 'line', smooth: true, symbol: 'circle', symbolSize: 5,
          lineStyle: { width: 2, color: PALETTE.brand[600] }, itemStyle: { color: PALETTE.brand[600] },
          data: [...history.map((h) => Number(h.total)), ...forecast.map(() => null)],
        },
        {
          name: 'Forecast', type: 'line', smooth: true, symbol: 'none',
          lineStyle: { width: 2, type: 'dashed', color: PALETTE.harvest[500] ?? PALETTE.harvest[400] },
          itemStyle: { color: PALETTE.harvest[500] ?? PALETTE.harvest[400] },
          // Joined to the last actual point so the two lines meet instead of floating apart.
          data: [...history.map((_, i) => (i === history.length - 1 ? Number(history[i].total) : null)), ...forecast.map((f: any) => Number(f.forecast))],
          // One reference line and one marked day — both switched on from their own KPI, so the chart
          // stays clean until someone asks the question that needs them.
          markLine: showAvg && !histOnly ? {
            silent: true, symbol: 'none',
            lineStyle: { color: PALETTE.sky[500], type: 'dashed', width: 1.5 },
            label: { formatter: `avg ${fmtShort(stats.avg)}`, color: token.colorTextSecondary, position: 'insideEndTop' },
            data: [{ yAxis: stats.avg }],
          } : undefined,
          markPoint: focusPeak && stats.peak && !histOnly ? {
            symbolSize: 44,
            itemStyle: { color: PALETTE.harvest[400] },
            label: { color: token.colorText, fontSize: 10, formatter: () => fmtShort(stats.peak.forecast) },
            data: [{ name: 'Busiest', xAxis: day10(stats.peak.date), yAxis: Number(stats.peak.forecast) }],
          } : undefined,
        },
      ],
    };
  }, [data, history, forecast, token, isQty, showAvg, showPrev, histOnly, focusPeak, stats]);

  /**
   * The day rows, each carrying what the day MEANS rather than just its number: how it compares with
   * an average day, where it sits in the running total, and how much of the horizon it accounts for.
   * A column of 30 similar figures answers "what will Tuesday be?" and nothing else — these answer
   * "which days matter, and when do we cross the number I care about?".
   */
  const tableRows = useMemo(() => {
    const max = forecast.reduce((m: number, f: any) => Math.max(m, Number(f.forecast ?? 0)), 0);
    let cum = 0;
    return forecast.map((f: any, i: number) => {
      const v = Number(f.forecast ?? 0);
      cum += v;
      const d = dayjs(day10(f.date));
      // The band is v ± margin; the upper half is untouched, so it is the honest measure of the
      // margin even where the lower half was clipped at zero.
      const margin = Math.max(0, Number(f.upper ?? 0) - v);
      return {
        key: i,
        ...f,
        value: v,
        margin,
        clipped: Number(f.lower ?? 0) <= 0 && v - margin < 0,
        cumulative: cum,
        share: stats.total > 0 ? (v / stats.total) * 100 : 0,
        vsAvg: stats.avg > 0 ? ((v - stats.avg) / stats.avg) * 100 : 0,
        bar: max > 0 ? (v / max) * 100 : 0,
        weekend: d.day() === 0, // Sunday — the day-of-week shape the model applies is most visible here
        isPeak: !!stats.peak && f.date === stats.peak.date,
      };
    });
  }, [forecast, stats]);

  return (
    <div>
      <PageHeader
        icon={<TrendingUp />}
        title="Sales Forecast"
        subtitle="Project the next few weeks from the last 90 days of orders — for the whole business, or for one product."
      />

      <Card className="mb-4">
        {/* Four controls on ONE baseline. Each cell is label-over-control and nothing else — the
            explanatory line used to live under the first control only, which pushed that column
            taller than its neighbours and left the whole row stepped. It sits under the row now. */}
        <div className="flex flex-wrap items-end gap-3">
          {/* Fixed, modest widths. Stretching the product box across a 1900px screen made a search
              field the width of the page for values that are ten characters long. */}
          <div className="w-[240px]">
            <label className="mb-1.5 block type-label-strong text-content">Product</label>
            <Select
              allowClear placeholder="Everything" className="w-full"
              value={familyKey} onChange={onFamily} showSearch optionFilterProp="label"
              options={families.map((f) => ({
                value: f.key,
                // The family name ALONE. It used to read "AGN 500ml · AGN" — the SKU already contains
                // the product name, so printing both said the same word twice on every row.
                label: f.name,
                count: f.variants.length,
                image: f.image,
              }))}
              optionRender={(o: any) => {
                const d = o.data ?? o;
                return (
                  <span className="flex min-w-0 items-center gap-2">
                    <EntityAvatar name={d.label} src={d.image} square size={26} icon={<Package size={13} />} />
                    <span className="min-w-0 flex-1 truncate">{d.label}</span>
                    {d.count > 1 && <span className="shrink-0 type-caption text-subtle">{d.count} sizes</span>}
                  </span>
                );
              }}
            />
          </div>
          <div className="w-[170px]">
            <label className="mb-1.5 block type-label-strong text-content">Size</label>
            <Select
              allowClear className="w-full"
              placeholder={familyKey ? 'All sizes' : 'Pick a product'}
              disabled={!familyKey}
              value={productId} onChange={setProductId} showSearch optionFilterProp="label"
              options={variants.map((v: any) => ({
                value: String(v.id),
                // Just the size. The family is already chosen and named in the box to the left.
                label: v.variantLabel || v.name,
                image: productImage(v),
              }))}
              optionRender={(o: any) => {
                const d = o.data ?? o;
                return (
                  <span className="flex min-w-0 items-center gap-2">
                    <EntityAvatar name={selFamily?.name ?? d.label} src={d.image} square size={22} icon={<Package size={11} />} />
                    <span className="min-w-0 flex-1 truncate">{d.label}</span>
                  </span>
                );
              }}
            />
          </div>
          {/* `min-w-0` so this column may shrink: as a flex item it defaults to `min-width:auto` and
              sized to the five-segment strip's natural width, pushing the PAGE 19px sideways at 320px.
              Segmented already caps and scrolls itself — it just needs a parent that can be narrower
              than its content. */}
          <div className="min-w-0 max-w-full">
            <label className="mb-1.5 block type-label-strong text-content">How far ahead</label>
            <Segmented
              value={String(days)}
              onChange={(v) => setDays(Number(v))}
              options={HORIZONS.map((d) => ({ value: String(d), label: `${d}d` }))}
            />
          </div>
          <Button variant="primary" onClick={run} loading={running} icon={<Play size={16} />}>
            Run forecast
          </Button>
        </div>

        {/* The measure changes with the product control, so it is spelled out right under it. */}
        <p className="mt-2.5 type-caption text-muted">
          {productId
            ? 'One variant selected — the forecast is in units of that item.'
            : familyKey
              ? 'A product with no variant chosen — pick a size, or leave it for order value across the business.'
              : 'No product chosen — the forecast is in order value (₹) across the whole business.'}
        </p>
      </Card>

      {data?.note && (
        <Alert
          className="mb-4" type="warning" icon={<TriangleAlert size={16} />}
          title={data.note}
          description="At least 7 days with orders are needed before a trend can be projected. Widen the product filter, or come back once more days have been booked."
        />
      )}

      {data && !!forecast.length && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
            <StatCard
              tone="brand" icon={<Sigma size={18} />}
              label={`Next ${ran?.days ?? days} days`}
              value={fmtShort(stats.total)}
              hint="see the days that add up to it"
              onClick={() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            />
            <StatCard
              tone="sky" icon={<Gauge size={18} />} label="Per day on average"
              value={fmtShort(stats.avg)}
              hint={showAvg ? 'drawn on the chart — click to hide' : `across the ${forecast.length}-day horizon · show on chart`}
              active={showAvg}
              onClick={() => setShowAvg((v) => !v)}
            />
            <StatCard
              tone={stats.change == null ? 'neutral' : stats.change >= 0 ? 'brand' : 'danger'}
              icon={stats.change == null ? <Minus size={18} /> : stats.change >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
              label="Against the same span behind"
              value={stats.change == null ? '—' : `${stats.change >= 0 ? '+' : ''}${stats.change.toFixed(0)}%`}
              hint={stats.change == null
                ? 'no comparable history to measure against'
                : showPrev
                  ? `the ${fmtShort(stats.prior)} it is compared with is on the chart`
                  : `last ${forecast.length} days ran ${fmtShort(stats.prior)} · overlay it`}
              active={showPrev}
              // Nothing to overlay when there is no comparable span — so it is not offered.
              onClick={stats.change == null ? undefined : () => setShowPrev((v) => !v)}
              delta={stats.change == null ? undefined : { label: `${stats.change >= 0 ? '+' : ''}${stats.change.toFixed(0)}%`, direction: stats.change >= 0 ? 'up' : 'down' }}
            />
            <StatCard
              tone="gold" icon={<CalendarRange size={18} />} label="Busiest day ahead"
              value={stats.peak ? dayjs(day10(stats.peak.date)).format('ddd D MMM') : '—'}
              hint={stats.peak
                ? (focusPeak ? `${fmtShort(stats.peak.forecast)} expected — marked below` : `${fmtShort(stats.peak.forecast)} expected · mark it`)
                : 'no peak identified'}
              active={focusPeak}
              onClick={stats.peak ? () => {
                setFocusPeak((v) => !v);
                if (!focusPeak) tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } : undefined}
            />
            <StatCard
              tone="neutral" icon={<History size={18} />} label="History it learned from"
              value={`${history.length} days`}
              hint={stats.histFrom
                ? (histOnly ? 'showing history only — click for the forecast' : `${dayjs(stats.histFrom).format('D MMM')} – ${dayjs(stats.histTo).format('D MMM')} · see it alone`)
                : 'no history'}
              active={histOnly}
              onClick={history.length ? () => setHistOnly((v) => !v) : undefined}
            />
          </div>

          {/* A forecast whose uncertainty dwarfs it is not a forecast, and saying so is the single most
              useful thing this page can do with sparse data. */}
          {stats.shaky && (
            <Alert
              className="mb-4" type="warning" icon={<TriangleAlert size={16} />}
              title="Treat this as a direction, not a number"
              description={`The likely range averages ${fmtShort(stats.bandWidth)} a day — wider than the forecast itself — because the ${history.length} days of history are few and uneven. Order days are sparse in this data, so a single large order moves the whole projection.`}
            />
          )}

          <Card
            className="mb-4"
            title={
              <span className="inline-flex flex-wrap items-center gap-2">
                {ran?.productLabel ?? 'All products'}
                <Tag tone={isQty ? 'cyan' : 'green'}>{isQty ? 'units' : '₹ order value'}</Tag>
                <Tooltip label="Level from the last 7 days, trend from the 7 days before that, shaped by day of week. The band is ±1.96 standard deviations — a 95% range.">
                  <span className="inline-flex items-center gap-1 type-caption text-subtle">
                    <Info size={12} /> {data.model ?? 'model'}
                  </span>
                </Tooltip>
              </span>
            }
          >
            {option && <ReactECharts option={option} style={{ height: 360 }} notMerge />}
            <p className="mt-1 type-caption text-muted">
              Solid green is what actually happened; the dashed gold line continues it, inside the shaded
              range the model considers likely.
            </p>
          </Card>

          {/* The scroll anchor lives on a wrapper: Card is a styled box, not a ref-forwarding element. */}
          <div ref={tableRef}>
          <Card title={`Day by day · next ${forecast.length} days`}>
            <Table
              rowKey="key"
              size="small"
              dataSource={tableRows}
              // Five short columns on a wide screen left the row two-thirds empty with everything
              // crammed left. `layoutFixed` shares the leftover width out in proportion to the widths
              // below, so the columns spread across the card instead of huddling.
              layoutFixed
              pagination={tableRows.length > 15 ? { pageSize: 15 } : false}
              rowClassName={(r: any) => (focusPeak && r.isPeak ? 'bg-harvest-400/10' : '')}
              columns={[
                {
                  title: 'Date', dataIndex: 'date', width: 190,
                  render: (v: string, r: any) => (
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      <span className={r.weekend ? 'text-muted' : ''}>{dayjs(day10(v)).format('ddd D MMM YYYY')}</span>
                      {r.isPeak && <Tag tone="gold">busiest</Tag>}
                    </span>
                  ),
                },
                {
                  // The bar makes the day-of-week shape visible down the column — the thing that
                  // actually drives these numbers — without adding a second chart.
                  title: `Forecast (${unitLabel})`, dataIndex: 'value', align: 'right' as const, width: 200,
                  render: (v: any, r: any) => (
                    <div className="flex items-center justify-end gap-2">
                      <span className="h-1.5 w-16 overflow-hidden rounded-pill bg-surface-3" aria-hidden>
                        <span
                          className={`block h-full rounded-pill ${r.isPeak ? 'bg-harvest-400' : 'bg-brand-500/60'}`}
                          style={{ width: `${Math.max(2, r.bar)}%` }}
                        />
                      </span>
                      <b className={`tabular-nums ${r.isPeak ? 'text-brand-700 dark:text-brand-300' : ''}`}>{fmt(v)}</b>
                    </div>
                  ),
                },
                {
                  // Every percentage on this page names its BASE in the heading. "+203%" with no
                  // denominator is a number the reader has to reverse-engineer — of what, exactly?
                  title: <span className="whitespace-nowrap">vs an average day<br /><span className="type-caption font-normal text-subtle">({fmtShort(stats.avg)}/day)</span></span>,
                  dataIndex: 'vsAvg', align: 'right' as const, width: 140,
                  render: (v: number) => (
                    Math.abs(v) < 0.5
                      ? <span className="type-caption text-subtle">about average</span>
                      : (
                        <span className={`tabular-nums ${v > 0 ? 'text-brand-700 dark:text-brand-300' : 'text-danger-600 dark:text-danger-500'}`}>
                          {v > 0 ? '+' : ''}{v.toFixed(0)}%
                        </span>
                      )
                  ),
                },
                {
                  // "When do we cross the number I care about?" — the question a per-day column alone
                  // makes the reader add up in their head. The share is cumulative too, so the two
                  // numbers in this cell describe the SAME thing (see: a row's numbers must agree).
                  // The PAIR, not the quotient: "₹4.2L of ₹39.2L" answers "how much out of how much"
                  // without the reader reversing a percentage.
                  title: 'Running total',
                  dataIndex: 'cumulative', align: 'right' as const, width: 210,
                  render: (v: number, r: any) => (
                    <div>
                      <div className="tabular-nums">
                        {fmt(v)} <span className="text-subtle">of {fmtShort(stats.total)}</span>
                      </div>
                      <div className="type-caption tabular-nums text-subtle">
                        day {r.key + 1} of {forecast.length} · {stats.total > 0 ? ((v / stats.total) * 100).toFixed(0) : 0}%
                      </div>
                    </div>
                  ),
                },
                {
                  /*
                   * WHY THE RANGE LOOKS LOPSIDED.
                   *
                   * The band is symmetric by construction — forecast ± 1.96 standard deviations — but
                   * the lower end is floored at zero, because a day cannot sell a negative amount. So
                   * "₹0 – ₹3.4L" around a ₹1.7L forecast is not a bug and not an asymmetric model: it
                   * is the same ± margin with its bottom clipped. Showing the margin, and saying when
                   * it was clipped, is the difference between a number that looks wrong and one that
                   * explains itself.
                   */
                  title: <span className="whitespace-nowrap">Likely range<br /><span className="type-caption font-normal text-subtle">(95% confidence)</span></span>,
                  dataIndex: 'lower', align: 'right' as const, width: 250,
                  render: (_: any, r: any) => (
                    <div>
                      <div className="whitespace-nowrap tabular-nums text-muted">{fmt(r.lower)} – {fmt(r.upper)}</div>
                      <div className="type-caption tabular-nums text-subtle">
                        ± {fmtShort(r.margin)}
                        {r.clipped && <span> · floor held at 0</span>}
                      </div>
                    </div>
                  ),
                },
              ] as any}
            />
          </Card>
          </div>
        </>
      )}

      {/* Only after the first run has actually finished — otherwise "Run a forecast" flashes on load
          under a page that is already running one. */}
      {booted && !running && !data && (
        <Card>
          <div className="py-10 text-center">
            <span className="mx-auto mb-3 grid size-12 place-items-center rounded-pill bg-brand-500/10 text-brand-700 dark:text-brand-300">
              <TrendingUp size={22} />
            </span>
            <div className="type-section-title">Run a forecast</div>
            <p className="mx-auto mt-1 max-w-[560px] type-body text-muted">
              Leave the product empty for total order value across the business, or pick one product to
              project the units it will sell. The last 90 days of orders are what the projection learns from.
            </p>
            <Button className="mt-4" variant="primary" onClick={run} loading={running} icon={<Play size={16} />}>
              Run forecast
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
